import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import useStyles from "./styles";
import api from "../../../services/api";
import alvoId from "../alvoId";
import rotuloAlvo from "../rotuloAlvo";

// Palavra que precisa ser digitada, exatamente, pra habilitar o botao de confirmacao -
// comparacao case-sensitive, sem trim: e a fricao extra desta acao, a mais perigosa da feature
const PALAVRA_CONFIRMACAO = "FORÇAR";

// Pra um alvo (cliente + proxy), lista os scripts "Desatualizado" (troca) e "Ausente no cliente" (instala) dele - e
// exatamente o que a atualizacao desse alvo vai baixar (mesmo criterio do backend). Mesma logica de
// modalAtualizarScripts/index.js, sem estado compartilhado entre os dois
function scriptsQueVaoMudar(scripts, alvo) {
	return (scripts || [])
		.map((script) => {
			const doCliente = script?.clientes?.find((c) => alvoId(c) === alvoId(alvo));
			if (doCliente?.status !== "Desatualizado" && doCliente?.status !== "Ausente no cliente") return null;
			return {
				chave: script?.chave,
				nome: script?.nome,
				versaoAtual: doCliente?.versaoCliente,
				versaoReferencia: script?.versaoReferencia,
			};
		})
		.filter(Boolean);
}

export default function ModalForcarScripts({
	open,
	onClose,
	clienteId,
	proxy,
	nomeCliente,
	motivoFalha,
	chaveSolicitada,
	scripts,
	onConcluido,
}) {
	const [textoConfirmacao, setTextoConfirmacao] = useState("");
	const [enviando, setEnviando] = useState(false);
	const [resultado, setResultado] = useState(null);
	const [erroEnvio, setErroEnvio] = useState(null);
	const theme = useTheme();
	const styles = useStyles(theme);

	// Reseta o estado toda vez que o modal reabre - resultado de uma tentativa anterior nao pode vazar
	useEffect(() => {
		if (!open) return;
		setTextoConfirmacao("");
		setEnviando(false);
		setResultado(null);
		setErroEnvio(null);
	}, [open]);

	// POST /versionamento/scripts/forcar - sempre ignorarDivergencia:true no backend, sempre 1 alvo
	// (cliente + proxy, "" = principal) por chamada. Com chave, forca so aquele script; sem chave, o alvo inteiro
	async function handleForcarAtualizacao() {
		setEnviando(true);
		setErroEnvio(null);
		try {
			const response = await api.post("/versionamento/scripts/forcar", {
				clienteId,
				proxy: proxy ?? "",
				chave: chaveSolicitada || null,
			});
			setResultado(response?.data || null);
		} catch (error) {
			setErroEnvio(
				error?.response?.data?.error ||
					"Não foi possível enviar a atualização forçada. Tente novamente.",
			);
		} finally {
			setEnviando(false);
		}
	}

	// Fecha o modal - se ja havia resultado (forcar enviado), dispara o refresh do pai antes
	function handleFechar() {
		if (enviando) return;
		if (resultado) {
			onConcluido?.();
		}
		onClose?.();
	}

	// Com chave, o forcar e de um script so (acao por script); a lista mostra so ele. Sem chave, alvo inteiro
	const afetados = chaveSolicitada
		? scriptsQueVaoMudar(scripts, { clienteId, proxy }).filter((script) => script.chave === chaveSolicitada)
		: scriptsQueVaoMudar(scripts, { clienteId, proxy });
	const scriptAlvo = chaveSolicitada
		? (scripts || []).find((script) => script?.chave === chaveSolicitada)
		: null;
	const nomeScriptAlvo = scriptAlvo?.nome || chaveSolicitada;
	const confirmacaoHabilitada = textoConfirmacao === PALAVRA_CONFIRMACAO;
	// Trecho " no proxy X" do texto de risco - vazio quando o alvo e o host principal do cliente
	const trechoProxy = proxy ? <> no proxy <strong>{proxy}</strong></> : null;

	return (
		<Dialog open={open} onClose={handleFechar} fullWidth maxWidth="sm">
			<DialogTitle sx={styles.tituloSevero}>
				<WarningAmber fontSize="small" />
				{chaveSolicitada
					? `Forçar atualização do script ${nomeScriptAlvo} — ação de risco`
					: "Forçar atualização de scripts — ação de risco"}
			</DialogTitle>
			<DialogContent>
				<Typography variant="body2" sx={styles.subtitulo}>
					{rotuloAlvo({ nome: nomeCliente, proxy })}
				</Typography>
				{resultado === null && (
					<Box>
						{chaveSolicitada ? (
							<Typography variant="body2" sx={styles.textoPrincipal}>
								Isso vai baixar e aplicar o script{" "}
								<strong>{nomeScriptAlvo}</strong> no cliente{" "}
								<strong>{nomeCliente}</strong>{trechoProxy} mesmo assim, ignorando o motivo de
								bloqueio abaixo. Só este script é tocado — os outros do cliente não
								são afetados.
							</Typography>
						) : (
							<Typography variant="body2" sx={styles.textoPrincipal}>
								Isso vai executar a atualização de scripts no cliente{" "}
								<strong>{nomeCliente}</strong>{trechoProxy} mesmo assim, ignorando o motivo de
								bloqueio abaixo. A execução é uma só para {proxy ? "o proxy" : "o cliente"} inteiro: baixa
								todos os scripts desatualizados dele e instala os que faltam.
							</Typography>
						)}
						{motivoFalha && (
							<Box sx={styles.blocoAlerta}>
								<Typography variant="subtitle2" sx={styles.textoAlerta}>
									O que está sendo ignorado
								</Typography>
								<Typography variant="body2" sx={styles.textoAlerta}>
									{motivoFalha}
								</Typography>
							</Box>
						)}
						<Typography variant="subtitle2" sx={styles.tituloAfetados}>
							{chaveSolicitada ? "Script que será tocado" : "Scripts que serão tocados neste cliente"}
						</Typography>
						{afetados.length === 0 ? (
							<Typography variant="body2" sx={styles.semItens}>
								{chaveSolicitada
									? `${nomeScriptAlvo} já está atualizado neste cliente — a execução não vai baixar nada.`
									: "Nenhum script deste cliente está desatualizado ou ausente no momento — a execução não vai baixar nada."}
							</Typography>
						) : (
							<Box component="ul" sx={styles.lista}>
								{afetados.map((script) => (
									<Box component="li" key={script.chave} sx={styles.itemLista}>
										{script.nome || script.chave}: {script.versaoAtual ?? "não instalado"} →{" "}
										{script.versaoReferencia}
									</Box>
								))}
							</Box>
						)}
						<Box sx={styles.confirmacaoContainer}>
							<Typography variant="body2" sx={styles.textoConfirmacao}>
								Para confirmar, digite {PALAVRA_CONFIRMACAO} no campo abaixo.
							</Typography>
							<TextField
								size="small"
								value={textoConfirmacao}
								disabled={enviando}
								onChange={(event) => setTextoConfirmacao(event.target.value)}
								placeholder={PALAVRA_CONFIRMACAO}
							/>
						</Box>
						{erroEnvio && (
							<Typography variant="body2" sx={styles.erro}>
								{erroEnvio}
							</Typography>
						)}
					</Box>
				)}
				{resultado !== null && (
					<Box sx={styles.resultadoContainer}>
						<Chip
							size="small"
							label={resultado?.status}
							sx={{
								...styles.chip,
								backgroundColor:
									resultado?.status === "sucesso"
										? theme.palette.chip.background.success
										: theme.palette.chip.background.critical,
								color:
									resultado?.status === "sucesso"
										? theme.palette.chip.colorText.success
										: theme.palette.chip.colorText.critical,
							}}
						/>
						{resultado?.status !== "sucesso" && resultado?.motivo && (
							<Typography variant="body2" sx={styles.motivoResultado}>
								{resultado.motivo}
							</Typography>
						)}
						{resultado?.status === "sucesso" && resultado?.recoletaAviso && (
							<Typography variant="body2" sx={styles.avisoResultado}>
								{resultado.recoletaAviso}
							</Typography>
						)}
						{resultado?.status === "sucesso" && resultado?.aviso && (
							<Typography variant="body2" sx={styles.avisoResultado}>
								{resultado.aviso}
							</Typography>
						)}
						{(resultado?.scriptsAtualizados || []).length > 0 && (
							<Box component="ul" sx={styles.lista}>
								{resultado.scriptsAtualizados.map((linha) => (
									<Box component="li" key={linha.chave} sx={styles.itemLista}>
										{linha.chave}: {linha.versaoAnterior ?? "não instalado"} →{" "}
										{linha.versaoFinal ?? "—"}
									</Box>
								))}
							</Box>
						)}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{resultado === null ? (
					<>
						<Button onClick={handleFechar} disabled={enviando}>
							Cancelar
						</Button>
						<Button
							variant="contained"
							sx={styles.botaoForcar}
							disabled={enviando || !confirmacaoHabilitada}
							onClick={handleForcarAtualizacao}
						>
							{enviando ? <CircularProgress size={20} /> : "Forçar atualização"}
						</Button>
					</>
				) : (
					<Button variant="contained" onClick={handleFechar}>
						Fechar
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
